import { memo, useMemo, type ReactNode } from 'react';
import { Tooltip } from 'antd';
import { formatByUnit, formatSignedByUnit, type MetricFormatUnit } from '@/shared/utils/format';

const TOOLTIP_DELAY_MS = 280;

/** Tiny pointer icon — avoids pulling `lucide-react` into this frequently-rendered chart. */
function DrillClickHintIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14 4.1 12 12l-4-1 1 4 9 3 3-9-9-3.9Z" />
      <path d="m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3Z" />
    </svg>
  );
}

export type MetricBulletChartUnit = MetricFormatUnit;

export interface BandOverlay {
  min: number;
  max: number;
  label: string;
  style?: 'solid' | 'dashed';
}

export interface NearBoundMarkers {
  peerMinInBand?: number;
  peerMaxInBand?: number;
  closestBelowSchool?: number;
  closestAboveSchool?: number;
}

/** Budget ids for opening the budget summary modal from chart markers. */
export interface DrillIds {
  actualBudgetId?: string;
  minBudgetId?: string;
  maxBudgetId?: string;
  minBudgetSchoolName?: string;
  maxBudgetSchoolName?: string;
}

export type BudgetDrillOpenMeta = { schoolName?: string };

export interface MetricBulletChartProps {
  label: string;
  unit: MetricBulletChartUnit;
  domainMin: number;
  domainMax: number;
  actual: number;
  avg: number;
  median: number;
  rawMin?: number;
  rawMax?: number;
  n?: number;
  overlays?: BandOverlay[];
  nearBounds?: NearBoundMarkers;
  /** Typical peer span (e.g. p10–p90) drawn inside the main scale for context. */
  typicalPeerBand?: { min: number; max: number };
  className?: string;
  /** When true, footer explains that the scale was widened past p10–p90 to fit markers. */
  expandedNote?: boolean;
  headerRight?: ReactNode;
  drill?: DrillIds;
  onOpenBudget?: (budgetId: string, meta?: BudgetDrillOpenMeta) => void;
  /** Appended to the chart aria-label (e.g. rank semantics). */
  rankAriaNote?: string;
  /** School profile: axis is full peer min–max (not display-only zoom). */
  peerDistributionScale?: boolean;
}

const TRACK = '#e5e7eb';
const COLOR_ACTUAL = '#2563eb';
const COLOR_AVG = '#16a34a';
const COLOR_MEDIAN = '#f59e0b';

function finite(n: number | undefined): n is number {
  return n !== undefined && typeof n === 'number' && Number.isFinite(n);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function pctOnTrack(v: number, lo: number, hi: number): number {
  const span = hi - lo;
  if (!(span > 0) || !Number.isFinite(v)) return 50;
  return ((clamp(v, lo, hi) - lo) / span) * 100;
}

/** Clamp overlay [a,b] to domain and return left/width as % of track. */
function overlayRect(
  bandMin: number,
  bandMax: number,
  domainLo: number,
  domainHi: number
): { left: number; width: number } | null {
  if (!(domainHi > domainLo)) return null;
  const a0 = Math.min(bandMin, bandMax);
  const b0 = Math.max(bandMin, bandMax);
  const a = clamp(a0, domainLo, domainHi);
  const b = clamp(b0, domainLo, domainHi);
    if (!(b > a)) return null;
  const left = ((a - domainLo) / (domainHi - domainLo)) * 100;
  const width = ((b - a) / (domainHi - domainLo)) * 100;
  return { left, width: Math.max(width, 0.35) };
}

function tooltipContent(
  label: string,
  unit: MetricFormatUnit,
  actual: number,
  avg: number,
  median: number,
  lo: number,
  hi: number
): ReactNode {
  const row = (k: string, v: number) => (
    <div key={k} className="flex justify-between gap-4">
      <span className="text-slate-400">{k}</span>
      <span className="font-mono tabular-nums text-slate-100">{formatByUnit(v, unit)}</span>
    </div>
  );
  return (
    <div className="min-w-[10rem] space-y-1 text-xs">
      <div className="font-semibold text-white">{label}</div>
      {row('Actual', actual)}
      {row('Peer average', avg)}
      {row('Peer median', median)}
      <div className="border-t border-slate-600 pt-1 text-[10px] text-slate-400">
        Chart scale: {formatByUnit(lo, unit)} – {formatByUnit(hi, unit)}
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-slate-400">Δ to Avg</span>
        <span className="font-mono tabular-nums text-slate-100">{formatSignedByUnit(actual - avg, unit)}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-slate-400">Δ to Median</span>
        <span className="font-mono tabular-nums text-slate-100">{formatSignedByUnit(actual - median, unit)}</span>
      </div>
    </div>
  );
}

interface MarkerProps {
  pct: number;
  color: string;
  label: string;
  rawVal: number;
  unit: MetricFormatUnit;
  tip: ReactNode;
  z: number;
  value: number;
  domainLo: number;
  domainHi: number;
  /** Opens budget summary when set. */
  openBudgetOnClick?: () => void;
  /** Missing budget id while drill-down is enabled for this marker. */
  budgetUnavailableHint?: boolean;
  /** When the actual marker sits on a peer edge, place the Budget chip above to avoid overlapping peer pills. */
  budgetChipPlacement?: 'above' | 'below';
}

function VerticalMarker({
  pct,
  color,
  label,
  rawVal,
  unit,
  tip,
  z,
  value,
  domainLo,
  domainHi,
  openBudgetOnClick,
  budgetUnavailableHint,
  budgetChipPlacement = 'below',
}: MarkerProps) {
  const clampLow = value < domainLo;
  const clampHigh = value > domainHi;
  const drillTitle =
    openBudgetOnClick || budgetUnavailableHint ? (
      <div className="min-w-[10rem] space-y-1 text-xs">
        {tip}
        {openBudgetOnClick ? (
          <div className="border-t border-slate-600 pt-1 font-semibold text-emerald-100">
            <span className="inline-flex items-center gap-1">
              <DrillClickHintIcon className="size-3.5 shrink-0 opacity-95" />
              Click to open budget summary
            </span>
          </div>
        ) : null}
        {budgetUnavailableHint && !openBudgetOnClick ? (
          <div className="border-t border-slate-600 pt-1 font-medium text-amber-200/95">No budget available</div>
        ) : null}
      </div>
    ) : (
      tip
    );
  const cursorClass = openBudgetOnClick ? 'cursor-pointer' : budgetUnavailableHint ? 'cursor-not-allowed' : '';
  const drillRing =
    openBudgetOnClick != null
      ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-white shadow-[0_0_0_1px_rgba(37,99,235,0.35)] hover:ring-blue-600'
      : budgetUnavailableHint
        ? 'ring-2 ring-amber-300 ring-offset-1 ring-offset-white opacity-80'
        : '';
  return (
    <Tooltip title={drillTitle} mouseEnterDelay={TOOLTIP_DELAY_MS}>
      <span className={`inline-flex ${budgetUnavailableHint ? 'cursor-not-allowed' : ''}`}>
        <button
          type="button"
          tabIndex={0}
          disabled={!!budgetUnavailableHint}
          onClick={openBudgetOnClick ? () => openBudgetOnClick() : undefined}
          className={`absolute bottom-0 top-0 z-[12] flex w-9 min-w-[2.25rem] -translate-x-1/2 flex-col items-center justify-center rounded outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-60 ${cursorClass} ${drillRing}`}
          style={{ left: `${pct}%`, zIndex: z }}
          aria-label={`${label} ${formatByUnit(rawVal, unit)}${openBudgetOnClick ? ', click to open budget summary' : ''}${clampLow ? ', at scale low edge' : ''}${clampHigh ? ', at scale high edge' : ''}`}
        >
          {clampLow ? (
            <span className="pointer-events-none absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 pr-0.5 text-[9px] font-bold text-slate-700" aria-hidden>
              ◀
            </span>
          ) : null}
          {clampHigh ? (
            <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 translate-x-full pl-0.5 text-[9px] font-bold text-slate-700" aria-hidden>
              ▶
            </span>
          ) : null}
          <span
            className="absolute inset-y-1 left-1/2 w-[3px] -translate-x-1/2 rounded-full shadow-md ring-1 ring-black/10"
            style={{ backgroundColor: color }}
          />
          <span
            className="absolute left-1/2 top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md ring-1 ring-black/15"
            style={{ backgroundColor: color }}
          />
          {openBudgetOnClick && budgetChipPlacement === 'above' ? (
            <svg
              className="pointer-events-none absolute left-1/2 top-0 h-[50%] max-h-36 w-12 -translate-x-1/2 overflow-visible"
              viewBox="0 0 48 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              <line
                x1="24"
                y1="100"
                x2="24"
                y2="6"
                stroke="#60a5fa"
                strokeWidth="2.25"
                strokeLinecap="round"
                opacity={0.85}
              />
              <circle cx="24" cy="6" r="3.25" fill="#60a5fa" opacity={0.95} />
            </svg>
          ) : null}
          {openBudgetOnClick ? (
            <span
              className={`pointer-events-none absolute left-1/2 z-[1] flex -translate-x-1/2 items-center gap-0.5 rounded-full border border-blue-200 bg-blue-50 px-1 py-px text-[8px] font-bold uppercase tracking-wide text-blue-800 shadow-sm ring-1 ring-blue-100 ${
                budgetChipPlacement === 'above'
                  ? 'bottom-full mb-1'
                  : '-bottom-0.5 translate-y-full'
              }`}
            >
              <DrillClickHintIcon className="size-2.5 shrink-0" />
              Budget
            </span>
          ) : null}
        </button>
      </span>
    </Tooltip>
  );
}

interface PeerDrillTickProps {
  pct: number;
  side: 'min' | 'max';
  rawVal: number;
  unit: MetricFormatUnit;
  openBudgetOnClick?: () => void;
  budgetUnavailableHint: boolean;
  /** Horizontal nudge (px) so the pill does not sit under the actual marker when both align on an edge. */
  nudgeX?: number;
}

function PeerDrillTick({
  pct,
  side,
  rawVal,
  unit,
  openBudgetOnClick,
  budgetUnavailableHint,
  nudgeX = 0,
}: PeerDrillTickProps) {
  const edgeLabel = side === 'min' ? 'Lowest peer' : 'Highest peer';
  const title =
    openBudgetOnClick || budgetUnavailableHint ? (
      <div className="min-w-[9rem] space-y-1 text-xs">
        <div className="font-semibold text-white">{edgeLabel}</div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-400">Value</span>
          <span className="font-mono tabular-nums text-slate-100">{formatByUnit(rawVal, unit)}</span>
        </div>
        {openBudgetOnClick ? (
          <div className="border-t border-slate-600 pt-1 font-semibold text-emerald-100">
            <span className="inline-flex items-center gap-1">
              <DrillClickHintIcon className="size-3.5 shrink-0 opacity-95" />
              Click to open budget summary
            </span>
          </div>
        ) : null}
        {budgetUnavailableHint && !openBudgetOnClick ? (
          <div className="border-t border-slate-600 pt-1 font-medium text-amber-200/95">No budget available</div>
        ) : null}
      </div>
    ) : (
      <div className="text-xs">
        <div className="font-semibold text-white">{edgeLabel}</div>
        <div className="font-mono text-slate-100">{formatByUnit(rawVal, unit)}</div>
      </div>
    );
  const tx = `calc(-50% + ${side === 'min' ? nudgeX : -nudgeX}px)`;
  const showLeader = Math.abs(nudgeX) > 0.5 && !!openBudgetOnClick;
  const cx = 26;
  const spread = Math.min(22, 10 + nudgeX * 0.32);
  const xTop = side === 'min' ? cx - spread : cx + spread;

  return (
    <Tooltip title={title} mouseEnterDelay={TOOLTIP_DELAY_MS}>
      <span
        className={`absolute z-[14] flex flex-col items-center ${
          openBudgetOnClick ? 'cursor-pointer' : budgetUnavailableHint ? 'cursor-not-allowed opacity-80' : ''
        }`}
        style={{ left: `${pct}%`, top: 'calc(42% + 0.875rem + 2px)', transform: `translateX(${tx})` }}
      >
        {showLeader ? (
          <svg
            width="52"
            height="38"
            viewBox="0 0 52 38"
            className="pointer-events-none -mb-0.5 -mt-5 shrink-0 overflow-visible"
            aria-hidden
          >
            <line
              x1={xTop}
              y1="3"
              x2={cx}
              y2="35"
              stroke="#2563eb"
              strokeWidth="2.25"
              strokeLinecap="round"
            />
            <circle cx={xTop} cy="3" r="3.5" fill="#2563eb" />
          </svg>
        ) : null}
        {openBudgetOnClick ? (
          <button
            type="button"
            className="flex min-w-[3.25rem] flex-col items-center rounded-md border-2 border-blue-400 bg-blue-50/95 px-1.5 py-1 shadow-md outline-none ring-2 ring-blue-400/40 ring-offset-1 ring-offset-white transition hover:border-blue-500 hover:bg-blue-100 focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label={`${edgeLabel} ${formatByUnit(rawVal, unit)}, click to open budget summary`}
            onClick={() => openBudgetOnClick()}
          >
            <span className="flex items-center gap-0.5 text-[8px] font-extrabold uppercase tracking-wide text-blue-900">
              <DrillClickHintIcon className="size-3 shrink-0" />
              Budget
            </span>
            <span className="mt-0.5 max-w-[5rem] text-center text-[7px] font-semibold uppercase leading-tight text-slate-700">
              {edgeLabel}
            </span>
          </button>
        ) : (
          <span
            className="flex flex-col items-center rounded-md border border-dashed border-slate-300 bg-slate-50/90 px-1.5 py-1"
            tabIndex={budgetUnavailableHint ? 0 : -1}
            aria-label={`${edgeLabel} ${formatByUnit(rawVal, unit)}${budgetUnavailableHint ? ', no budget available' : ''}`}
          >
            <span className="text-[7px] font-semibold uppercase text-slate-500">{edgeLabel}</span>
          </span>
        )}
      </span>
    </Tooltip>
  );
}

/**
 * Single-scale peer bullet chart: typical peer range (p10–p90) with optional tuition band overlays and near-bound ticks.
 */
function MetricBulletChartInner({
  label,
  unit,
  domainMin: lo,
  domainMax: hi,
  actual,
  avg,
  median,
  rawMin,
  rawMax,
  n,
  overlays,
  nearBounds,
  typicalPeerBand,
  className = '',
  expandedNote = false,
  headerRight,
  drill,
  onOpenBudget,
  rankAriaNote,
  peerDistributionScale = false,
}: MetricBulletChartProps) {
  const domainOk = finite(lo) && finite(hi) && hi > lo;
  const insufficientPeers = n !== undefined && n < 5;
  const drillActive = !!(drill && onOpenBudget);
  const peerMinMaxDrill = drillActive && (n ?? 0) >= 2;
  const actualBudgetOpen =
    drillActive && drill?.actualBudgetId ? () => onOpenBudget!(drill.actualBudgetId!) : undefined;
  const actualBudgetMissing = drillActive && !drill?.actualBudgetId;
  const minBudgetOpen =
    peerMinMaxDrill && drill?.minBudgetId
      ? () =>
          onOpenBudget!(drill.minBudgetId!, drill.minBudgetSchoolName ? { schoolName: drill.minBudgetSchoolName } : undefined)
      : undefined;
  const minBudgetMissing = peerMinMaxDrill && !drill?.minBudgetId;
  const maxBudgetOpen =
    peerMinMaxDrill && drill?.maxBudgetId
      ? () =>
          onOpenBudget!(drill.maxBudgetId!, drill.maxBudgetSchoolName ? { schoolName: drill.maxBudgetSchoolName } : undefined)
      : undefined;
  const maxBudgetMissing = peerMinMaxDrill && !drill?.maxBudgetId;

  const a = finite(actual) ? actual : undefined;
  const av = finite(avg) ? avg : undefined;
  const med = finite(median) ? median : undefined;

  const tip = useMemo(() => {
    if (!domainOk || !finite(actual) || !finite(avg) || !finite(median)) return null;
    return tooltipContent(label, unit, actual, avg, median, lo, hi);
  }, [domainOk, label, unit, actual, avg, median, lo, hi]);

  const positions = useMemo(() => {
    if (!domainOk) return null;
    return {
      actual: a !== undefined ? pctOnTrack(a, lo, hi) : null,
      avg: av !== undefined ? pctOnTrack(av, lo, hi) : null,
      median: med !== undefined ? pctOnTrack(med, lo, hi) : null,
    };
  }, [domainOk, lo, hi, a, av, med]);

  const typicalBandLayout = useMemo(() => {
    if (!domainOk || !typicalPeerBand) return null;
    const { min: tMin, max: tMax } = typicalPeerBand;
    if (!Number.isFinite(tMin) || !Number.isFinite(tMax) || !(tMax > tMin)) return null;
    return overlayRect(tMin, tMax, lo, hi);
  }, [domainOk, typicalPeerBand, lo, hi]);

  const overlayLayouts = useMemo(() => {
    if (!domainOk || !overlays?.length) return [];
    const dashed = overlays.filter((o) => o.style === 'dashed');
    const solid = overlays.filter((o) => o.style !== 'dashed');
    const ordered = [...dashed, ...solid];
    return ordered
      .map((o, i) => {
        const geom = overlayRect(o.min, o.max, lo, hi);
        if (!geom) return null;
        return { ...o, ...geom, key: `${o.label}-${i}` };
      })
      .filter((x): x is NonNullable<typeof x> => x != null);
  }, [domainOk, overlays, lo, hi]);

  const rawTickPct = useMemo(() => {
    if (!domainOk) return { left: null as number | null, right: null as number | null };
    return {
      left: finite(rawMin) ? pctOnTrack(rawMin, lo, hi) : null,
      right: finite(rawMax) ? pctOnTrack(rawMax, lo, hi) : null,
    };
  }, [domainOk, lo, hi, rawMin, rawMax]);

  const nearPct = useMemo(() => {
    if (!domainOk || !nearBounds) return null;
    const mapVal = (v?: number) => (finite(v) ? pctOnTrack(v, lo, hi) : null);
    return {
      peerMin: mapVal(nearBounds.peerMinInBand),
      peerMax: mapVal(nearBounds.peerMaxInBand),
      below: mapVal(nearBounds.closestBelowSchool),
      above: mapVal(nearBounds.closestAboveSchool),
    };
  }, [domainOk, lo, hi, nearBounds]);

  /** Keep budget drill controls readable when actual aligns with lowest/highest peer on the scale. */
  const budgetDrillLayout = useMemo(() => {
    const defaultLayout = {
      budgetChipPlacement: 'below' as const,
      minNudgeX: 0,
      maxNudgeX: 0,
      hideMinPeerBudget: false,
      hideMaxPeerBudget: false,
    };
    if (!domainOk || !drillActive || !positions || positions.actual == null || !peerMinMaxDrill) {
      return defaultLayout;
    }
    const ap = positions.actual;
    const left = rawTickPct.left;
    const right = rawTickPct.right;
    const thr = 9;
    let budgetChipPlacement: 'above' | 'below' = 'below';
    let minNudgeX = 0;
    let maxNudgeX = 0;
    let hideMinPeerBudget = false;
    let hideMaxPeerBudget = false;

    const nearRight = right != null && Math.abs(ap - right) < thr;
    const nearLeft = left != null && Math.abs(ap - left) < thr;

    if (nearRight) {
      budgetChipPlacement = 'above';
      if (drill?.actualBudgetId && drill?.maxBudgetId && drill.actualBudgetId === drill.maxBudgetId) {
        hideMaxPeerBudget = true;
      } else {
        maxNudgeX = 46;
      }
    }
    if (nearLeft) {
      budgetChipPlacement = 'above';
      if (drill?.actualBudgetId && drill?.minBudgetId && drill.actualBudgetId === drill.minBudgetId) {
        hideMinPeerBudget = true;
      } else {
        minNudgeX = 46;
      }
    }

    return { budgetChipPlacement, minNudgeX, maxNudgeX, hideMinPeerBudget, hideMaxPeerBudget };
  }, [
    domainOk,
    drillActive,
    positions,
    peerMinMaxDrill,
    rawTickPct.left,
    rawTickPct.right,
    drill?.actualBudgetId,
    drill?.minBudgetId,
    drill?.maxBudgetId,
  ]);

  const ariaSummary = useMemo(() => {
    if (!domainOk) return `${label}: range unavailable`;
    const peerEnds =
      finite(rawMin) && finite(rawMax)
        ? `peer min ${formatByUnit(rawMin!, unit)}, peer max ${formatByUnit(rawMax!, unit)}`
        : '';
    return [
      label,
      `scale ${formatByUnit(lo, unit)} to ${formatByUnit(hi, unit)}`,
      peerEnds,
      a !== undefined ? `actual ${formatByUnit(a, unit)}` : '',
      av !== undefined ? `average ${formatByUnit(av, unit)}` : '',
      med !== undefined ? `median ${formatByUnit(med, unit)}` : '',
      n != null ? `n ${n}` : '',
      rankAriaNote?.trim() ?? '',
    ]
      .filter(Boolean)
      .join(', ');
  }, [domainOk, label, unit, lo, hi, a, av, med, n, rawMin, rawMax, rankAriaNote]);

  if (!domainOk || !positions || !tip) {
    return (
      <div
        className={`rounded-lg border border-slate-200 bg-slate-50 px-3 py-4 text-center text-xs text-slate-500 ${className}`}
        role="img"
        aria-label={`${label}: range unavailable`}
      >
        Range unavailable
      </div>
    );
  }

  const dMed = finite(actual) && finite(median) ? actual - median : NaN;
  const dAvg = finite(actual) && finite(avg) ? actual - avg : NaN;

  return (
    <div
      className={`w-full min-w-0 ${insufficientPeers ? 'opacity-60' : ''} ${className}`}
      role="img"
      aria-label={ariaSummary}
    >
      {insufficientPeers ? (
        <p className="mb-2 rounded-md bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-950 ring-1 ring-amber-200/80">
          Insufficient peers (n smaller than 5); treat as indicative.
        </p>
      ) : null}
      <div className="mb-1 flex flex-wrap items-start justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-700">{label}</p>
        {headerRight ? <div className="shrink-0">{headerRight}</div> : null}
      </div>

      {a !== undefined && av !== undefined && med !== undefined ? (
        <div className="mb-2 space-y-1.5 rounded-md bg-slate-50/90 px-2 py-1.5 text-[10px] ring-1 ring-slate-200/80">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5">
            <span className="inline-flex items-baseline gap-1">
              <span className="font-bold uppercase tracking-wide text-[#1d4ed8]">Actual</span>
              <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">{formatByUnit(a, unit)}</span>
            </span>
            <span className="inline-flex items-baseline gap-1">
              <span className="font-bold uppercase tracking-wide text-[#15803d]">Avg</span>
              <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">{formatByUnit(av, unit)}</span>
            </span>
            <span className="inline-flex items-baseline gap-1">
              <span className="font-bold uppercase tracking-wide text-[#b45309]">Median</span>
              <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">{formatByUnit(med, unit)}</span>
            </span>
          </div>
          <p className="flex flex-wrap items-center gap-x-1 border-t border-slate-200/80 pt-1.5 text-[9px] font-medium leading-tight text-slate-500">
            <span className="shrink-0 rounded border border-slate-300/90 bg-slate-200/80 px-1 py-px font-bold uppercase tracking-wide text-slate-600">
              {peerDistributionScale ? 'Peer span' : 'Chart scale'}
            </span>
            <span className="font-mono tabular-nums text-slate-600">{formatByUnit(lo, unit)}</span>
            <span className="text-slate-400" aria-hidden>
              —
            </span>
            <span className="font-mono tabular-nums text-slate-600">{formatByUnit(hi, unit)}</span>
          </p>
        </div>
      ) : null}

      <div className="flex items-stretch gap-1.5 sm:gap-2">
        <span className="flex w-10 shrink-0 items-center justify-end self-center text-right text-[8px] font-mono tabular-nums leading-tight text-slate-600 sm:w-[3.25rem] sm:text-[9px]">
          {formatByUnit(lo, unit)}
        </span>
        <div
          className={`relative min-w-0 flex-1 ${
            drillActive && budgetDrillLayout.budgetChipPlacement === 'above'
              ? 'min-h-[7.85rem]'
              : drillActive
                ? 'min-h-[6.85rem]'
                : 'min-h-[5.5rem]'
          }`}
        >
          <div
            className="absolute left-0 right-0 top-[42%] h-3.5 -translate-y-1/2 rounded-md shadow-inner ring-1 ring-slate-400/50"
            style={{ backgroundColor: TRACK }}
          >
            {typicalBandLayout ? (
              <div
                className="pointer-events-none absolute top-0 z-0 h-full rounded-sm border border-dashed border-slate-400/60 bg-slate-400/[0.12]"
                style={{
                  left: `${typicalBandLayout.left}%`,
                  width: `${typicalBandLayout.width}%`,
                }}
                title="Typical peers (approx. p10–p90)"
                aria-hidden
              />
            ) : null}
            {overlayLayouts.map((ov) => {
              const isDashed = ov.style === 'dashed';
              return (
                <div
                  key={ov.key}
                  className={`pointer-events-none absolute top-0 z-[1] h-full rounded-sm ${
                    isDashed ? 'border border-dashed border-violet-600 bg-violet-600/[0.06]' : 'border border-blue-600 bg-blue-600/[0.10]'
                  }`}
                  style={{
                    left: `${ov.left}%`,
                    width: `${ov.width}%`,
                    borderColor: isDashed ? '#7c3aed' : '#2563eb',
                  }}
                  title={ov.label}
                  aria-hidden
                />
              );
            })}
            <div className="pointer-events-none absolute left-0 top-0 z-[2] h-full w-[3px] rounded-l-md bg-slate-500/85" aria-hidden />
            <div className="pointer-events-none absolute right-0 top-0 z-[2] h-full w-[3px] rounded-r-md bg-slate-500/85" aria-hidden />
            {rawTickPct.left != null && finite(rawMin) && !peerMinMaxDrill ? (
              <div
                className="pointer-events-none absolute top-full z-[2] mt-0.5 h-2 w-px -translate-x-1/2 bg-slate-400/70"
                style={{ left: `${rawTickPct.left}%` }}
                aria-hidden
              />
            ) : null}
            {rawTickPct.right != null && finite(rawMax) && !peerMinMaxDrill ? (
              <div
                className="pointer-events-none absolute top-full z-[2] mt-0.5 h-2 w-px -translate-x-1/2 bg-slate-400/70"
                style={{ left: `${rawTickPct.right}%` }}
                aria-hidden
              />
            ) : null}
            {nearPct?.peerMin != null ? (
              <div
                className="pointer-events-none absolute bottom-0 z-[3] h-2 w-px -translate-x-1/2 bg-slate-700/80"
                style={{ left: `${nearPct.peerMin}%` }}
                aria-hidden
              />
            ) : null}
            {nearPct?.peerMax != null ? (
              <div
                className="pointer-events-none absolute bottom-0 z-[3] h-2 w-px -translate-x-1/2 bg-slate-700/80"
                style={{ left: `${nearPct.peerMax}%` }}
                aria-hidden
              />
            ) : null}
          </div>

          {nearPct?.below != null ? (
            <div
              className="pointer-events-none absolute top-[calc(42%+0.75rem+2px)] z-[4] -translate-x-1/2 text-[8px] text-slate-600"
              style={{ left: `${nearPct.below}%` }}
              title="Closest peer tuition below this school (in band)"
              aria-hidden
            >
              <span className="block leading-none">▼</span>
            </div>
          ) : null}
          {nearPct?.above != null ? (
            <div
              className="pointer-events-none absolute top-[calc(42%+0.75rem+2px)] z-[4] -translate-x-1/2 text-[8px] text-slate-600"
              style={{ left: `${nearPct.above}%` }}
              title="Closest peer tuition above this school (in band)"
              aria-hidden
            >
              <span className="block leading-none">▲</span>
            </div>
          ) : null}

          <div className="pointer-events-none absolute inset-0 z-[10]">
            {positions.median != null && med !== undefined ? (
              <div className="pointer-events-auto">
                <VerticalMarker
                  pct={positions.median}
                  color={COLOR_MEDIAN}
                  label={`${label} median`}
                  rawVal={med}
                  unit={unit}
                  tip={tip}
                  z={3}
                  value={med}
                  domainLo={lo}
                  domainHi={hi}
                />
              </div>
            ) : null}
            {positions.avg != null && av !== undefined ? (
              <div className="pointer-events-auto">
                <VerticalMarker
                  pct={positions.avg}
                  color={COLOR_AVG}
                  label={`${label} average`}
                  rawVal={av}
                  unit={unit}
                  tip={tip}
                  z={4}
                  value={av}
                  domainLo={lo}
                  domainHi={hi}
                />
              </div>
            ) : null}
            {positions.actual != null && a !== undefined ? (
              <div className="pointer-events-auto">
                <VerticalMarker
                  pct={positions.actual}
                  color={COLOR_ACTUAL}
                  label={`${label} actual`}
                  rawVal={a}
                  unit={unit}
                  tip={tip}
                  z={5}
                  value={a}
                  domainLo={lo}
                  domainHi={hi}
                  openBudgetOnClick={actualBudgetOpen}
                  budgetUnavailableHint={actualBudgetMissing}
                  budgetChipPlacement={budgetDrillLayout.budgetChipPlacement}
                />
              </div>
            ) : null}
            {peerMinMaxDrill && rawTickPct.left != null && finite(rawMin) && !budgetDrillLayout.hideMinPeerBudget ? (
              <div className="pointer-events-auto">
                <PeerDrillTick
                  pct={rawTickPct.left}
                  side="min"
                  rawVal={rawMin}
                  unit={unit}
                  openBudgetOnClick={minBudgetOpen}
                  budgetUnavailableHint={minBudgetMissing}
                  nudgeX={budgetDrillLayout.minNudgeX}
                />
              </div>
            ) : null}
            {peerMinMaxDrill && rawTickPct.right != null && finite(rawMax) && !budgetDrillLayout.hideMaxPeerBudget ? (
              <div className="pointer-events-auto">
                <PeerDrillTick
                  pct={rawTickPct.right}
                  side="max"
                  rawVal={rawMax}
                  unit={unit}
                  openBudgetOnClick={maxBudgetOpen}
                  budgetUnavailableHint={maxBudgetMissing}
                  nudgeX={budgetDrillLayout.maxNudgeX}
                />
              </div>
            ) : null}
          </div>
        </div>
        <span className="flex w-10 shrink-0 items-center justify-start self-center text-left text-[8px] font-mono tabular-nums leading-tight text-slate-600 sm:w-[3.25rem] sm:text-[9px]">
          {formatByUnit(hi, unit)}
        </span>
      </div>

      {overlayLayouts.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[8px] text-slate-600">
          {overlayLayouts.map((ov) => (
            <span key={ov.key} className="inline-flex items-center gap-1">
              <span
                className="size-2 shrink-0 rounded-sm border"
                style={{
                  borderColor: ov.style === 'dashed' ? '#7c3aed' : '#2563eb',
                  backgroundColor: ov.style === 'dashed' ? 'rgba(124,58,237,0.12)' : 'rgba(37,99,235,0.15)',
                }}
                aria-hidden
              />
              {ov.label}
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-slate-100 pt-2 text-[9px] text-slate-700">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <span className="size-2.5 shrink-0 rounded-full border border-blue-700/25 shadow-sm" style={{ backgroundColor: COLOR_ACTUAL }} />
          Actual <span className="font-mono text-slate-900">{a !== undefined ? formatByUnit(a, unit) : '—'}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 font-medium">
          <span className="size-2.5 shrink-0 rounded-full border border-amber-700/25 shadow-sm" style={{ backgroundColor: COLOR_MEDIAN }} />
          Median <span className="font-mono text-slate-900">{med !== undefined ? formatByUnit(med, unit) : '—'}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 font-medium">
          <span className="size-2.5 shrink-0 rounded-full border border-emerald-700/25 shadow-sm" style={{ backgroundColor: COLOR_AVG }} />
          Avg <span className="font-mono text-slate-900">{av !== undefined ? formatByUnit(av, unit) : '—'}</span>
        </span>
        {finite(rawMin) && finite(rawMax) ? (
          <>
            <span className="inline-flex items-center gap-1.5 font-medium text-slate-500">
              <span className="h-3 w-px shrink-0 bg-slate-400/80" aria-hidden />
              Lowest peer
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium text-slate-500">
              <span className="h-3 w-px shrink-0 bg-slate-400/80" aria-hidden />
              Highest peer
            </span>
          </>
        ) : null}
        {Number.isFinite(dMed) ? (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[9px] font-semibold text-slate-800 ring-1 ring-slate-200">
            ΔMedian {formatSignedByUnit(dMed, unit)}
          </span>
        ) : null}
        {Number.isFinite(dAvg) ? (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[9px] font-semibold text-slate-800 ring-1 ring-slate-200">
            ΔAvg {formatSignedByUnit(dAvg, unit)}
          </span>
        ) : null}
      </div>

      <p className="mt-2 text-[9px] leading-snug text-slate-500">
        {peerDistributionScale
          ? `Scale spans all peers (min–max) with slight padding.${
              typicalPeerBand && typicalBandLayout ? ' Dashed band ≈ typical peers (p10–p90).' : ''
            } `
          : typicalPeerBand && typicalBandLayout
            ? 'Shaded band ≈ typical peers (p10–p90). Scale spans all peers (min–max) with slight padding. '
            : expandedNote
              ? 'Range shows typical peers (p10–p90). Range auto-expanded to include markers. '
              : 'Range shows typical peers (p10–p90). '}
        {n != null ? `n = ${n.toLocaleString()}.` : ''}
        {finite(rawMin) && finite(rawMax)
          ? ` Lowest / highest peer: ${formatByUnit(rawMin, unit)} – ${formatByUnit(rawMax, unit)}.`
          : ''}
      </p>
      {drillActive ? (
        <p className="mt-2 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/95 px-2.5 py-2 text-[10px] font-semibold leading-snug text-blue-950 ring-1 ring-blue-100">
          <DrillClickHintIcon className="mt-0.5 size-4 shrink-0 text-blue-700" />
          <span>
            <span className="uppercase tracking-wide text-blue-800">Budget drill-down</span> — Markers with a{' '}
            <strong className="font-extrabold">blue ring</strong>, the <strong className="font-extrabold">Budget</strong>{' '}
            chip on the actual value, or the blue <strong className="font-extrabold">Budget</strong> pill on lowest/highest
            peers are <strong className="font-extrabold">clickable</strong> and open the budget summary. When your school
            lines up with a peer extreme, the chart shifts labels so nothing stacks on the same spot; if the same budget
            would open twice, only one pill is shown.
          </span>
        </p>
      ) : null}
    </div>
  );
}

export const MetricBulletChart = memo(MetricBulletChartInner);
